import {Node} from '@tiptap/vue-3'
import {Plugin, PluginKey} from 'prosemirror-state'

const Image = Node.create({
    name: 'image',

    addOptions() {
        return {
            allowBase64: false,
            uploadCallback: null
        }
    },

    group: 'block',

    draggable: true,

    addAttributes() {
        return {
            src: {},
            alt: {
                default: null,
            },
            title: {
                default: null,
            },
            ratio: {
                default: null,
            },
            path: {
                default: null,
            },
        }
    },

    parseHTML() {
        return [
            {
                tag: this.options.allowBase64 ? 'img[src]' : 'img[src]:not([src^="data:"])',
                getAttrs: element => ({
                    src: element.getAttribute('src'),
                    title: element.getAttribute('title'),
                    alt: element.getAttribute('alt'),
                    style: element.getAttribute('style'),
                    path: element.getAttribute('data-spoferan-path'),
                    ratio: element.getAttribute('data-spoferan-ratio'),
                }),
            }
        ]
    },

    renderHTML({HTMLAttributes}) {
        // We must wrap the image within a div to apply alignment styling
        // TODO: This must still be enabled in FormCodearea, but implementation was paused due to display issues in UserContent
        return ['div', {
            style: HTMLAttributes.style,
            class: 'image-wrapper'
        }, ['img', {
            src: HTMLAttributes.src,
            title: HTMLAttributes.title,
            alt: HTMLAttributes.alt,
            style: HTMLAttributes.style, // Required on the img tag as well to be able to extract them again
            'data-spoferan-path': HTMLAttributes.path,
            'data-spoferan-ratio': HTMLAttributes.ratio,
        }]]
    },

    addCommands() {
        return {
            setImage: options => ({commands}) => {
                return commands.insertContent({
                    type: this.name,
                    attrs: options,
                })
            },
        }
    },

    addProseMirrorPlugins() {
        const uploadCallback = this.options.uploadCallback;

        return [
            new Plugin({
                key: new PluginKey('image'),
                props: {
                    handleDrop(view, event) {
                        if (!uploadCallback) {
                            return;
                        }

                        const hasFiles = event.dataTransfer
                            && event.dataTransfer.files
                            && event.dataTransfer.files.length

                        if (!hasFiles) {
                            return
                        }

                        const images = Array
                            .from(event.dataTransfer.files)
                            .filter(file => (/image/i).test(file.type))

                        if (images.length === 0) {
                            return
                        }

                        event.preventDefault()

                        const {schema} = view.state
                        const coordinates = view.posAtCoords({left: event.clientX, top: event.clientY})

                        images.forEach(image => {
                            uploadCallback(image).then(data => {
                                const node = schema.nodes.image.create({
                                    src: data.src,
                                    title: data.title,
                                    alt: data.alt,
                                    path: data.path,
                                    ratio: data.ratio,
                                })
                                const transaction = view.state.tr.insert(coordinates.pos, node)
                                view.dispatch(transaction)
                            });
                        })
                    },
                    handlePaste(view, event) {
                        if (!uploadCallback) {
                            return;
                        }

                        const hasFiles = event.clipboardData
                            && event.clipboardData.files
                            && event.clipboardData.files.length

                        if (!hasFiles) {
                            return
                        }

                        const images = Array
                            .from(event.clipboardData.files)
                            .filter(file => (/image/i).test(file.type))

                        if (images.length === 0) {
                            return
                        }

                        // The clipboard data is stacked on every paste -> only get the latest
                        const imageFile = images[images.length - 1];

                        const {schema, selection} = view.state
                        const position = selection.$cursor ? selection.$cursor.pos : selection.$to.pos

                        event.preventDefault()

                        uploadCallback(imageFile).then(data => {
                            const node = schema.nodes.image.create({
                                src: data.src,
                                title: data.title,
                                alt: data.alt,
                                path: data.path,
                                ratio: data.ratio,
                            })
                            const transaction = view.state.tr.insert(position, node)
                            view.dispatch(transaction)
                        });
                    }
                },
            }),
        ]
    },
});

export default Image;
